// controllers/invoiceController.js  (frontend)
import apiService from '../services/apiService';
import { authController } from './authController';

// ─── Internal helpers (same pattern as subscriptionController) ────────────────
const _getUser = () => authController.getCurrentUser();
const _getRole = () => _getUser()?.role || null;
const _getUid  = () => { const u = _getUser(); return u?.id || u?._id || null; };

const _guard = (allowedRoles) => {
  const role = _getRole();
  if (!role)                        return { blocked: true, reason: 'Not authenticated. Please log in.' };
  if (!allowedRoles.includes(role)) return { blocked: true, reason: `Access denied. Requires: ${allowedRoles.join(' or ')}` };
  return { blocked: false };
};

// ─── Invoice Controller ───────────────────────────────────────────────────────
const invoiceController = {

  // ── Auth helpers ────────────────────────────────────────────────────────────
  getCurrentUser: () => _getUser(),
  getCurrentRole: () => _getRole(),
  isSuperAdmin:   () => _getRole() === 'super_admin',
  isAdmin:        () => ['admin', 'super_admin'].includes(_getRole()),

  // ── GET all invoices ────────────────────────────────────────────────────────
  /**
   * GET /api/invoices
   * @param {Object} filters - invoiceType, status, userId, startDate, endDate,
   *                           minAmount, maxAmount, search, page, limit, sortBy, sortOrder
   */
  getAllInvoices: async (filters = {}) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const qs  = new URLSearchParams(filters).toString();
      const res = await apiService.get(qs ? `invoices?${qs}` : 'invoices');
      return { success: true, data: res.data || [], pagination: res.pagination || {} };
    } catch (e) { return { success: false, data: [], message: e.message || 'Failed to fetch invoices' }; }
  },

  // ── GET invoices by current user ────────────────────────────────────────────
  /**
   * GET /api/invoices/user/:userId
   */
  getMyInvoices: async (filters = {}) => {
    const g   = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    const uid = _getUid();
    if (!uid) return { success: false, data: [], message: 'User not found' };
    try {
      const qs  = new URLSearchParams(filters).toString();
      const res = await apiService.get(qs ? `invoices/user/${uid}?${qs}` : `invoices/user/${uid}`);
      return { success: true, data: res.data || [], pagination: res.pagination || {} };
    } catch (e) { return { success: false, data: [], message: e.message || 'Failed to fetch invoices' }; }
  },

  // ── GET invoices for a specific user (admin) ────────────────────────────────
  getInvoicesByUser: async (userId, filters = {}) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const qs  = new URLSearchParams(filters).toString();
      const res = await apiService.get(qs ? `invoices/user/${userId}?${qs}` : `invoices/user/${userId}`);
      return { success: true, data: res.data || [], pagination: res.pagination || {} };
    } catch (e) { return { success: false, data: [], message: e.message || 'Failed to fetch user invoices' }; }
  },

  // ── GET single invoice ──────────────────────────────────────────────────────
  getInvoiceById: async (id) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const res = await apiService.get(`invoices/${id}`);
      return { success: true, data: res.data };
    } catch (e) { return { success: false, data: null, message: e.message || 'Failed to fetch invoice' }; }
  },

  // ── GET by invoice number ───────────────────────────────────────────────────
  getInvoiceByNumber: async (invoiceNumber) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const res = await apiService.get(`invoices/number/${invoiceNumber}`);
      return { success: true, data: res.data };
    } catch (e) { return { success: false, data: null, message: e.message || 'Invoice not found' }; }
  },

  // ── CREATE generic ──────────────────────────────────────────────────────────
  createInvoice: async (invoiceData) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post('invoices', invoiceData);
      return { success: true, data: res.data, message: 'Invoice created successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to create invoice' }; }
  },

  // ── CREATE subscription invoice ─────────────────────────────────────────────
  createSubscriptionInvoice: async (data) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post('invoices/subscription', data);
      return { success: true, data: res.data, message: 'Subscription invoice created' };
    } catch (e) { return { success: false, message: e.message || 'Failed to create subscription invoice' }; }
  },

  // ── CREATE key invoice ──────────────────────────────────────────────────────
  createKeyInvoice: async (data) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post('invoices/key', data);
      return { success: true, data: res.data, message: 'Key invoice created' };
    } catch (e) { return { success: false, message: e.message || 'Failed to create key invoice' }; }
  },

  // ── CREATE product invoice ──────────────────────────────────────────────────
  createProductInvoice: async (data) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post('invoices/product', data);
      return { success: true, data: res.data, message: 'Product invoice created' };
    } catch (e) { return { success: false, message: e.message || 'Failed to create product invoice' }; }
  },

  // ── UPDATE invoice ──────────────────────────────────────────────────────────
  updateInvoice: async (id, updates) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.put(`invoices/${id}`, updates);
      return { success: true, data: res.data, message: 'Invoice updated successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to update invoice' }; }
  },

  // ── UPDATE status ───────────────────────────────────────────────────────────
  updateStatus: async (id, status) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`invoices/${id}/status`, { status });
      return { success: true, data: res.data, message: 'Status updated successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to update status' }; }
  },

  // ── SEND invoice ────────────────────────────────────────────────────────────
  sendInvoice: async (id) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`invoices/${id}/send`, {});
      return { success: true, data: res.data, message: 'Invoice sent successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to send invoice' }; }
  },

  // ── RECORD payment ──────────────────────────────────────────────────────────
  recordPayment: async (id, paymentData) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`invoices/${id}/payment`, paymentData);
      return { success: true, data: res.data, message: 'Payment recorded successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to record payment' }; }
  },

  // 🆕 ── MARK AS PAID (shortcut) ────────────────────────────────────────────────
  // Poora balance ek hi call mein clear karne ka shortcut — recordPayment ko
  // khud amount calculate karke bulana nahi padta, backend full-balance payment
  // + status:'Paid' ek saath handle karta hai
  markAsPaid: async (id, paymentMethod = 'Cash', reference = '') => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`invoices/${id}/mark-paid`, { paymentMethod, reference });
      return { success: true, data: res.data, message: 'Invoice marked as paid' };
    } catch (e) { return { success: false, message: e.message || 'Failed to mark as paid' }; }
  },

  // 🆕 ── GET payment history for an invoice ─────────────────────────────────────
  // Agar invoice pe multiple partial payments hue hain, unki poori list yahan se milegi
  getPaymentHistory: async (id) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const res = await apiService.get(`invoices/${id}/payments`);
      return { success: true, data: res.data || [] };
    } catch (e) { return { success: false, data: [], message: e.message || 'Failed to fetch payment history' }; }
  },

  // ── CANCEL invoice ──────────────────────────────────────────────────────────
  cancelInvoice: async (id, cancelReason = '') => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`invoices/${id}/cancel`, { cancelReason });
      return { success: true, data: res.data, message: 'Invoice cancelled successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to cancel invoice' }; }
  },

  // ── REFUND invoice ──────────────────────────────────────────────────────────
  refundInvoice: async (id, refundAmount, refundReason = '') => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`invoices/${id}/refund`, { refundAmount, refundReason });
      return { success: true, data: res.data, message: 'Refund processed successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to process refund' }; }
  },

  // 🆕 ── DUPLICATE invoice ───────────────────────────────────────────────────────
  // Recurring customer ke liye same items/amount se nayi draft invoice bana deta hai
  duplicateInvoice: async (id) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post(`invoices/${id}/duplicate`, {});
      return { success: true, data: res.data, message: 'Invoice duplicated successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to duplicate invoice' }; }
  },

  // ── DELETE invoice (super_admin only, draft only) ────────────────────────────
  deleteInvoice: async (id) => {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      await apiService.delete(`invoices/${id}`);
      return { success: true, message: 'Invoice deleted successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to delete invoice' }; }
  },

  // 🆕 ── BULK update status ──────────────────────────────────────────────────────
  // Multiple invoices select karke ek saath status badalne ke liye (list page checkboxes)
  bulkUpdateStatus: async (ids = [], status) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    if (!ids.length) return { success: false, message: 'No invoices selected' };
    try {
      const res = await apiService.patch('invoices/bulk/status', { ids, status });
      return { success: true, data: res.data, message: `${ids.length} invoice(s) updated` };
    } catch (e) { return { success: false, message: e.message || 'Bulk status update failed' }; }
  },

  // 🆕 ── BULK delete (super_admin only, draft only) ─────────────────────────────
  bulkDelete: async (ids = []) => {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    if (!ids.length) return { success: false, message: 'No invoices selected' };
    try {
      await apiService.post('invoices/bulk/delete', { ids });
      return { success: true, message: `${ids.length} invoice(s) deleted` };
    } catch (e) { return { success: false, message: e.message || 'Bulk delete failed' }; }
  },

  // ── GET statistics ──────────────────────────────────────────────────────────
  getStatistics: async (filters = {}) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const qs  = new URLSearchParams(filters).toString();
      const res = await apiService.get(qs ? `invoices/statistics?${qs}` : 'invoices/statistics');
      return { success: true, data: res.data };
    } catch (e) { return { success: false, data: null, message: e.message }; }
  },

  // 🆕 ── GET revenue summary (charts ke liye — daily/monthly grouped totals) ────
  getRevenueSummary: async (filters = {}) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const qs  = new URLSearchParams(filters).toString();
      const res = await apiService.get(qs ? `invoices/revenue-summary?${qs}` : 'invoices/revenue-summary');
      return { success: true, data: res.data };
    } catch (e) { return { success: false, data: null, message: e.message || 'Failed to fetch revenue summary' }; }
  },

  // ── GET overdue invoices ────────────────────────────────────────────────────
  getOverdueInvoices: async () => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const res = await apiService.get('invoices/overdue');
      return { success: true, data: res.data || [] };
    } catch (e) { return { success: false, data: [], message: e.message }; }
  },

  // 🆕 ── GET invoices due soon (default: agle 7 din mein due) ──────────────────
  // Follow-up reminders ya dashboard "upcoming dues" widget ke liye useful
  getDueSoon: async (days = 7) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const res = await apiService.get(`invoices/due-soon?days=${days}`);
      return { success: true, data: res.data || [] };
    } catch (e) { return { success: false, data: [], message: e.message || 'Failed to fetch upcoming dues' }; }
  },

  // ── EXPORT invoices ─────────────────────────────────────────────────────────
  exportInvoices: async (filters = {}, format = 'csv') => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const qs  = new URLSearchParams({ ...filters, format }).toString();
      const res = await apiService.get(`invoices/export?${qs}`);
      return { success: true, data: res.data };
    } catch (e) { return { success: false, message: e.message }; }
  },

  // 🆕 ── DOWNLOAD invoice PDF ─────────────────────────────────────────────────────
  // paymentController.downloadInvoice jaisa hi authenticated blob-fetch pattern —
  // Bearer token ya cookie dono auth mein reliably kaam karega
  downloadInvoicePdf: async (id, invoiceNumber) => {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const token   = authController.getToken();
      const baseUrl = (apiService.defaults?.baseURL || apiService.baseURL || process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const url     = `${baseUrl}/invoices/${id}/download`;

      const res = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });

      if (!res.ok) {
        let message = 'Failed to download invoice';
        try {
          const err = await res.json();
          message = err.message || message;
        } catch (e) { /* response JSON nahi tha, default message rakho */ }
        throw new Error(message);
      }

      const blob      = await res.blob();
      const objectUrl  = window.URL.createObjectURL(blob);
      const link       = document.createElement('a');
      link.href        = objectUrl;
      link.download    = `${invoiceNumber || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (e) { return { success: false, message: e.message || 'Download failed' }; }
  },
};

export default invoiceController;